import * as React from "react";
import { type ChangeProposal, type ApplyMode, type ChangeProposalState, runsToText } from "@lupp/shared";

export interface ChangeProposalSidebarProps {
  /** Docked beside the content (≥600px) vs. overlaying it — mirrors the history sidebar. */
  docked: boolean;
  proposals: ChangeProposal[];
  states: Record<string, ChangeProposalState>;
  applyMode: ApplyMode;
  onApplyModeChange: (mode: ApplyMode) => void;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onAcceptAll: () => void;
  onRejectAll: () => void;
  onScrollTo: (paragraphId: string) => void;
  onClose: () => void;
}

const APPLY_MODE_OPTIONS: { value: ApplyMode; label: string }[] = [
  { value: "with_tracked_changes", label: "Änderungsverfolgung" },
  { value: "with_tracked_changes_and_comment", label: "Änderungsverfolgung + Kommentar" },
  { value: "with_comment", label: "Direkt + Kommentar" },
  { value: "raw", label: "Direkt (ohne Tracking)" },
];

const applyModeLabel = (mode?: ApplyMode): string => APPLY_MODE_OPTIONS.find((o) => o.value === mode)?.label ?? "";

function operationLabel(op: ChangeProposal["operation"]): string {
  switch (op) {
    case "replace": return "Ersetzen";
    case "insert": return "Einfügen";
    case "delete": return "Löschen";
    case "format": return "Formatieren";
    case "cross_ref": return "Querverweis";
    case "insert_block": return "Abschnitt einfügen";
    default: return "Änderung";
  }
}

interface ChangeItemProps {
  proposal: ChangeProposal;
  num: number; // stabile Nummer (Eingangsreihenfolge), unabhängig von der Sortierung
  state?: ChangeProposalState;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onScrollTo: (paragraphId: string) => void;
}

/**
 * Ein Vorschlag in der Sidebar. Pending = aufgeklappt (Vorher/Nachher + Aktionen); sobald
 * angenommen/abgelehnt → automatisch zugeklappt (nur noch Beschreibung + Status). Der Chevron
 * oben rechts klappt manuell auf/zu. Vorher/Nachher erscheint NUR im aufgeklappten Zustand.
 */
class ChangeItem extends React.Component<ChangeItemProps, { collapsed: boolean }> {
  state = { collapsed: !!this.props.state };

  componentDidUpdate(prev: ChangeItemProps): void {
    // Beim Übergang pending → aufgelöst automatisch zuklappen.
    if (!prev.state && this.props.state) this.setState({ collapsed: true });
  }

  private toggle = (): void => this.setState((s) => ({ collapsed: !s.collapsed }));

  render(): JSX.Element {
    const { proposal: p, num, state, onAccept, onReject, onScrollTo } = this.props;
    const { collapsed } = this.state;
    const after = p.new_text ?? runsToText(p.content?.runs);

    return (
      <li className={`cp-item${state ? ` cp-item--${state.status}` : ""}`}>
        <div className="cp-item__head">
          <span className="cp-item__num">Änderung {num}</span>
          <span className="change-proposal-panel__op-badge">{operationLabel(p.operation)}</span>
          <button type="button" className="cp-item__jump" onClick={() => onScrollTo(p.paragraph_id)} title="Im Dokument anspringen" aria-label="Im Dokument anspringen">
            ↗
          </button>
          <span className="cp-item__spacer" />
          <button type="button" className="cp-item__chevron" onClick={this.toggle} aria-expanded={!collapsed} aria-label={collapsed ? "Aufklappen" : "Zuklappen"}>
            <svg className={`cp-item__chevron-icon${collapsed ? "" : " cp-item__chevron-icon--open"}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
        </div>

        {p.comment && <div className="change-proposal-panel__comment">{p.comment}</div>}

        {!collapsed && (
          <>
            {p.old_text && (
              <div className="change-proposal-panel__before">
                <span className="change-proposal-panel__label">Vorher</span>
                <span className="change-proposal-panel__text change-proposal-panel__text--old">{p.old_text}</span>
              </div>
            )}
            {after && (
              <div className="change-proposal-panel__after">
                <span className="change-proposal-panel__label">Nachher</span>
                <span className="change-proposal-panel__text change-proposal-panel__text--new">{after}</span>
              </div>
            )}
          </>
        )}

        {state ? (
          <div className={`change-proposal-panel__status change-proposal-panel__status--${state.status}`}>
            {state.status === "applied" ? `Übernommen · ${applyModeLabel(state.applyMode)}` : "Abgelehnt"}
          </div>
        ) : (
          <div className="change-proposal-panel__actions">
            <button type="button" className="change-proposal-panel__btn change-proposal-panel__btn--accept" onClick={() => onAccept(p.id)} aria-label="Übernehmen">
              Übernehmen
            </button>
            <button type="button" className="change-proposal-panel__btn change-proposal-panel__btn--reject" onClick={() => onReject(p.id)} aria-label="Ablehnen">
              Ablehnen
            </button>
          </div>
        )}
      </li>
    );
  }
}

export default function ChangeProposalSidebar({
  docked,
  proposals,
  states,
  applyMode,
  onApplyModeChange,
  onAccept,
  onReject,
  onAcceptAll,
  onRejectAll,
  onScrollTo,
  onClose,
}: ChangeProposalSidebarProps): JSX.Element {
  const pending = proposals.filter((p) => !states[p.id]);
  // Offene Vorschläge nach oben, aufgelöste nach unten (stabil → Eingangsreihenfolge bleibt je Gruppe).
  const ordered = proposals
    .map((p, i) => ({ p, num: i + 1 }))
    .sort((a, b) => (states[a.p.id] ? 1 : 0) - (states[b.p.id] ? 1 : 0));

  return (
    <aside className={`cp-sidebar${docked ? " cp-sidebar--docked" : " cp-sidebar--overlay"}`} aria-label="Änderungsvorschläge">
      <div className="cp-sidebar__header">
        <span className="cp-sidebar__title">
          {proposals.length} {proposals.length === 1 ? "Änderungsvorschlag" : "Änderungsvorschläge"}
        </span>
        <button className="icon-btn" type="button" aria-label="Vorschläge schließen" title="Schließen" onClick={onClose}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <label className="cp-sidebar__mode" title="Wie übernommene Änderungen ins Dokument geschrieben werden">
        <select className="cp-sidebar__mode-select" value={applyMode} onChange={(e): void => onApplyModeChange(e.target.value as ApplyMode)}>
          {APPLY_MODE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <div className="cp-sidebar__body">
        <ul className="cp-list">
          {ordered.map(({ p, num }) => (
            <ChangeItem key={p.id} proposal={p} num={num} state={states[p.id]} onAccept={onAccept} onReject={onReject} onScrollTo={onScrollTo} />
          ))}
        </ul>
      </div>

      {pending.length > 0 && (
        <div className="cp-sidebar__bulk">
          <button type="button" className="change-proposal-panel__btn change-proposal-panel__btn--accept-all" onClick={onAcceptAll} aria-label="Alle annehmen">
            Alle annehmen
          </button>
          <button type="button" className="change-proposal-panel__btn change-proposal-panel__btn--reject-all" onClick={onRejectAll} aria-label="Alle ablehnen">
            Alle ablehnen
          </button>
        </div>
      )}
    </aside>
  );
}
